import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRfiActions } from "./useRfiActions";

const EMPTY_FORM = { to_contact_id: "", response_due: "", intro: "" };
const EMPTY_CONTACT = { name: "", email: "", company: "" };
const draftQuestions = (findings, selected, withAction = true) =>
  Object.fromEntries(findings.filter((f) => selected.includes(f.id)).map((f) => [f.id, f.draft_rfi || `Please clarify: ${f.title}.${withAction ? ` ${f.recommended_action || ""}` : ""}`.trim()]));
const fail = (fallback) => (e) => toast.error(e?.response?.data?.detail || fallback);
const pad = (n, w) => String(n).padStart(w, "0");
const previewItem = (f, i, count) => ({ finding_id: f.id, rfi_no: `RFI-${pad(count + 1, 3)}.${pad(i + 1, 2)}`, finding_ref: f.id.slice(0, 8).toUpperCase(), title: f.title, severity: f.severity, source_section: f.source_section, source_page: f.source_page, quoted: f.relevant_language, drawing_ref: f.drawing_ref });

// State + loading for the RFI package dialog; per-package actions live in useRfiActions.
export function useRfiPackage({ projectId, selected, findings, open, onCreated }) {
  const { activeWorkspaceId } = useAuth();
  const [pkgs, setPkgs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [questions, setQuestions] = useState({});
  const [sends, setSends] = useState([]);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);

  const load = async () => {
    const [p, c, m] = await Promise.all([api.get(`/projects/${projectId}/rfi-packages`), api.get(`/projects/${projectId}/contacts`), api.get(`/workspaces/${activeWorkspaceId}/members`)]);
    setPkgs(p.data); setContacts(c.data); setMembers(m.data);
  };
  const startNew = (withAction) => { setActive(null); setForm(EMPTY_FORM); setQuestions(draftQuestions(findings, selected, withAction)); };
  useEffect(() => { if (open) { load(); startNew(true); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when the dialog opens

  const openPkg = async (id) => {
    const [{ data }, s] = await Promise.all([api.get(`/rfi-packages/${id}`), api.get(`/rfi-packages/${id}/sends`)]);
    setActive(data); setSends(s.data);
    setForm({ to_contact_id: data.to_contact_id || "", response_due: data.response_due || "", intro: data.intro || "" });
    setQuestions(Object.fromEntries(data.items.map((it) => [it.finding_id, it.question])));
  };
  const itemsPayload = () => Object.entries(questions).map(([finding_id, question]) => ({ finding_id, question }));
  const actions = useRfiActions({ active, openPkg, load, onCreated, form, itemsPayload });

  const create = () => actions.run("create", async () => {
    const { data } = await api.post(`/projects/${projectId}/rfi-packages`, { finding_ids: selected, to_contact_id: form.to_contact_id || null, response_due: form.response_due, intro: form.intro });
    await api.put(`/rfi-packages/${data.id}`, { items: itemsPayload() });
    toast.success(`${data.number} created with ${data.items.length} item${data.items.length > 1 ? "s" : ""}`);
    await load(); await openPkg(data.id); onCreated?.();
  }, "Could not create RFI package");
  const addContact = async () => {
    if (!contactForm.name || !contactForm.email) return toast.error("Name and email are required");
    try { const { data } = await api.post(`/projects/${projectId}/contacts`, contactForm); setContacts([...contacts, data]); setContactForm(EMPTY_CONTACT); toast.success("Contact added"); } catch (e) { fail("Could not add contact")(e); }
  };
  const remove = async (p) => { if (!window.confirm(`Delete ${p.number}?`)) return; await api.delete(`/rfi-packages/${p.id}`); toast.success("Deleted"); if (active?.id === p.id) setActive(null); load(); };

  const items = active ? active.items : findings.filter((f) => selected.includes(f.id)).map((f, i) => previewItem(f, i, pkgs.length));

  return { pkgs, contacts, members, active, form, setForm, questions, setQuestions, sends, contactForm, setContactForm, items, openPkg, startNew, create, addContact, remove, ...actions };
}
